import type { ActionType, ProColumns } from '@ant-design/pro-components';
import { ModalForm, PageContainer, ProFormText, ProTable } from '@ant-design/pro-components';
import { Button, Grid, message, Modal, Popconfirm, Tag } from 'antd';
import { useMemo, useRef, useState } from 'react';
import api from '../services/api';

type TeacherRow = {
  id: number;
  username: string;
  validUntil: string | Date | null;
  createdAt: string | Date;
  isVoided: boolean;
  voidedAt: string | Date | null;
  voidedByAdminUsername: string | null;
  isExpired: boolean;
  classCount: number;
};

type ClassRow = {
  id: number;
  name: string;
  teacherId: number;
  createdAt: string | Date;
  studentCount: number;
  lockScreenPassword?: string | null;
};

type StudentRow = {
  id: number;
  name: string;
  studentNumber: string | null;
  currentPoints: number;
  petType: string | null;
  badges: number;
  coins: number;
  createdAt: string | Date;
};

const formatDateTime = (value: unknown) => {
  if (!value) return '-';
  const date =
    value instanceof Date
      ? value
      : typeof value === 'string' || typeof value === 'number'
        ? new Date(value)
        : new Date(String(value));
  if (Number.isNaN(date.getTime())) return '-';

  const parts = new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);

  const map: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') map[p.type] = p.value;
  }
  return `${map.year}-${map.month}-${map.day} ${map.hour}:${map.minute}:${map.second}`;
};

export default function Teachers() {
  const actionRef = useRef<ActionType | undefined>(undefined);
  const [selectedTeacher, setSelectedTeacher] = useState<TeacherRow | null>(null);
  const [editingTeacher, setEditingTeacher] = useState<TeacherRow | null>(null);
  const [selectedClass, setSelectedClass] = useState<ClassRow | null>(null);
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;

  const teacherColumns: ProColumns<TeacherRow>[] = useMemo(
    () => [
      {
        title: '激活码',
        dataIndex: 'activationCode',
        hideInTable: true,
      },
      { title: 'ID', dataIndex: 'id', width: 80, search: false, hideInTable: isMobile },
      {
        title: '用户名',
        dataIndex: 'username',
        copyable: true,
        width: isMobile ? undefined : 220,
        search: isMobile ? false : undefined,
      },
      {
        title: '有效期类型',
        dataIndex: 'validityType',
        hideInTable: true,
        valueEnum: {
          day: { text: '一天' },
          year: { text: '一年' },
          permanent: { text: '永久' },
        },
      },
      {
        title: '状态',
        dataIndex: 'isVoided',
        width: 140,
        search: false,
        hideInTable: isMobile,
        valueEnum: {
          false: { text: '正常' },
          true: { text: '已作废' },
        },
        render: (_dom, record) => {
          if (record.isVoided) return <Tag>已作废</Tag>;
          if (record.isExpired) return <Tag color="red">已过期</Tag>;
          return <Tag color="green">正常</Tag>;
        },
      },
      {
        title: '是否作废',
        dataIndex: 'isVoided',
        hideInTable: true,
        valueEnum: {
          false: { text: '未作废' },
          true: { text: '已作废' },
        },
      },
      {
        title: '是否过期',
        dataIndex: 'expired',
        hideInTable: true,
        valueEnum: {
          false: { text: '未过期' },
          true: { text: '已过期' },
        },
      },
      {
        title: '到期时间',
        dataIndex: 'validUntil',
        search: false,
        hideInTable: isMobile,
        render: (_dom, record) => (record.validUntil ? formatDateTime(record.validUntil) : '永久'),
      },
      {
        title: '班级数',
        dataIndex: 'classCount',
        search: false,
        width: 100,
        hideInTable: isMobile,
      },
      {
        title: '创建时间',
        dataIndex: 'createdAt',
        valueType: 'dateTime',
        search: false,
        sorter: true,
        hideInTable: isMobile,
      },
      // {
      //   title: '作废时间',
      //   dataIndex: 'voidedAt',
      //   search: false,
      //   render: (_dom, record) => formatDateTime(record.voidedAt),
      // },
      // {
      //   title: '作废人',
      //   dataIndex: 'voidedByAdminUsername',
      //   search: false,
      //   render: (_dom, record) => record.voidedByAdminUsername ?? '-',
      // },
      {
        title: '操作',
        valueType: 'option',
        width: 220,
        render: (_dom, record) => (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <Button
              key="edit"
              type="link"
              disabled={record.isVoided}
              style={{ padding: 0 }}
              onClick={() => {
                setEditingTeacher(record);
              }}
            >
              修改
            </Button>
            {record.validUntil ? (
              <Popconfirm
                key="upgrade"
                title="确认升级该教师？"
                description="升级后该教师将成为永久用户，不再有有效期限制"
                okText="确认"
                cancelText="取消"
                disabled={record.isVoided}
                onConfirm={async () => {
                  try {
                    await api.post(`/admin/teachers/${record.id}/upgrade`);
                    message.success('升级成功');
                    actionRef.current?.reload();
                  } catch (e: unknown) {
                    const error = e as { response?: { data?: { message?: unknown } } };
                    const msg = error?.response?.data?.message;
                    message.error(typeof msg === 'string' ? msg : '升级失败');
                  }
                }}
              >
                <Button type="link" disabled={record.isVoided} style={{ padding: 0 }}>
                  升级
                </Button>
              </Popconfirm>
            ) : null}
            <Button
              key="classes"
              type="link"
              style={{ padding: 0 }}
              onClick={() => {
                setSelectedTeacher(record);
              }}
            >
              查看班级
            </Button>
            <Popconfirm
              key="void"
              title="确认作废该教师？"
              description="作废后该账号将无法登录"
              okText="确认"
              cancelText="取消"
              disabled={record.isVoided}
              onConfirm={async () => {
                try {
                  await api.post(`/admin/teachers/${record.id}/void`);
                  message.success('作废成功');
                  actionRef.current?.reload();
                } catch (e: unknown) {
                  const error = e as { response?: { data?: { message?: unknown } } };
                  const msg = error?.response?.data?.message;
                  message.error(typeof msg === 'string' ? msg : '作废失败');
                }
              }}
            >
              <Button type="link" danger disabled={record.isVoided} style={{ padding: 0 }}>
                作废
              </Button>
            </Popconfirm>
          </div>
        ),
      },
    ],
    [isMobile],
  );

  const classColumns: ProColumns<ClassRow>[] = useMemo(
    () => [
      { title: '班级ID', dataIndex: 'id', width: 100, search: false },
      { title: '班级名称', dataIndex: 'name', search: false },
      { title: '学生数', dataIndex: 'studentCount', width: 100, search: false },
      {
        title: '锁屏密码',
        dataIndex: 'lockScreenPassword',
        width: 120,
        search: false,
        render: (_dom, record) => record.lockScreenPassword || '-',
      },
      { title: '创建时间', dataIndex: 'createdAt', valueType: 'dateTime', search: false },
      {
        title: '操作',
        valueType: 'option',
        width: 120,
        render: (_dom, record) => [
          <Button
            key="students"
            type="link"
            onClick={() => {
              setSelectedClass(record);
            }}
          >
            查看学生
          </Button>,
        ],
      },
    ],
    [],
  );

  const studentColumns: ProColumns<StudentRow>[] = useMemo(
    () => [
      { title: '学生ID', dataIndex: 'id', width: 90, search: false },
      { title: '姓名', dataIndex: 'name' },
      { title: '学号', dataIndex: 'studentNumber', render: (_dom, r) => r.studentNumber ?? '-' },
      { title: '积分', dataIndex: 'currentPoints', search: false, width: 90 },
      { title: '宠物', dataIndex: 'petType', search: false, render: (_dom, r) => r.petType ?? '-' },
      { title: '徽章', dataIndex: 'badges', search: false, width: 90 },
      { title: '金币', dataIndex: 'coins', search: false, width: 90 },
      { title: '创建时间', dataIndex: 'createdAt', valueType: 'dateTime', search: false },
    ],
    [],
  );

  return (
    <PageContainer
      childrenContentStyle={
        isMobile
          ? {
              paddingInline: 12,
              paddingBlock: 12,
            }
          : undefined
      }
    >
      <ProTable<TeacherRow>
        columns={teacherColumns}
        actionRef={actionRef}
        cardBordered
        tableLayout="fixed"
        scroll={isMobile ? { x: 'max-content' } : undefined}
        search={{
          labelWidth: 'auto',
          span: isMobile ? 24 : undefined,
          defaultCollapsed: true,
        }}
        request={async (params: {
          current?: number;
          pageSize?: number;
          username?: string;
          isVoided?: boolean;
          expired?: boolean;
          activationCode?: string;
        } = {}) => {
          const res = await api.get('/admin/teachers', {
            params: {
              page: params.current,
              limit: params.pageSize,
              username: params.username,
              isVoided: params.isVoided,
              expired: params.expired,
              activationCode: params.activationCode,
            },
          });
          return {
            data: res.data.items,
            success: true,
            total: res.data.total,
          };
        }}
        rowKey="id"
        options={{ setting: { listsHeight: 400 } }}
        pagination={{ pageSize: 20 }}
        dateFormatter="string"
        headerTitle="教师列表"
      />

      <Modal
        open={!!selectedTeacher}
        title={selectedTeacher ? `班级列表（${selectedTeacher.username}）` : '班级列表'}
        onCancel={() => setSelectedTeacher(null)}
        footer={null}
        width={isMobile ? '100%' : 900}
        style={isMobile ? { top: 0, paddingBottom: 0, maxWidth: '100vw' } : undefined}
        bodyStyle={isMobile ? { padding: 12, maxHeight: 'calc(100dvh - 110px)', overflow: 'auto' } : undefined}
        destroyOnClose
      >
        <ProTable<ClassRow>
          columns={classColumns}
          search={false}
          options={false}
          tableLayout="fixed"
          scroll={isMobile ? { x: 'max-content' } : undefined}
          rowKey="id"
          pagination={false}
          params={{ teacherId: selectedTeacher?.id }}
          request={async () => {
            const teacherId = selectedTeacher?.id;
            if (!teacherId) {
              return { data: [], success: true, total: 0 };
            }
            const res = await api.get(`/admin/teachers/${teacherId}/classes`);
            return { data: res.data.items, success: true, total: res.data.items.length };
          }}
        />
      </Modal>

      <Modal
        open={!!selectedClass}
        title={selectedClass ? `学生列表（${selectedClass.name}）` : '学生列表'}
        onCancel={() => setSelectedClass(null)}
        footer={null}
        width={isMobile ? '100%' : 1100}
        style={isMobile ? { top: 0, paddingBottom: 0, maxWidth: '100vw' } : undefined}
        bodyStyle={isMobile ? { padding: 12, maxHeight: 'calc(100dvh - 110px)', overflow: 'auto' } : undefined}
        destroyOnClose
      >
        <ProTable<StudentRow>
          columns={studentColumns}
          cardBordered
          rowKey="id"
          options={{ setting: { listsHeight: 360 } }}
          pagination={{ pageSize: 50 }}
          tableLayout="fixed"
          scroll={isMobile ? { x: 'max-content' } : undefined}
          params={{ classId: selectedClass?.id }}
          request={async (params: {
            current?: number;
            pageSize?: number;
            name?: string;
            classId?: number;
          } = {}) => {
            const classId = selectedClass?.id;
            if (!classId) {
              return { data: [], success: true, total: 0 };
            }
            const res = await api.get(`/admin/classes/${classId}/students`, {
              params: {
                page: params.current,
                limit: params.pageSize,
                name: params.name,
              },
            });
            return { data: res.data.items, success: true, total: res.data.total };
          }}
        />
      </Modal>
      <ModalForm
        key={editingTeacher?.id}
        title="修改用户名"
        open={!!editingTeacher}
        modalProps={{
          destroyOnClose: true,
          onCancel: () => setEditingTeacher(null),
        }}
        initialValues={{ username: editingTeacher?.username }}
        onFinish={async (values) => {
          try {
            await api.post(`/admin/teachers/${editingTeacher?.id}/username`, {
              username: values.username,
            });
            message.success('修改成功');
            setEditingTeacher(null);
            actionRef.current?.reload();
            return true;
          } catch (e: unknown) {
            const error = e as { response?: { data?: { message?: unknown } } };
            const errorMsg = error.response?.data?.message || '修改失败';
            if (errorMsg === 'Username already exists') {
              message.error('用户名已存在');
            } else {
              message.error(typeof errorMsg === 'string' ? errorMsg : '修改失败');
            }
            return false;
          }
        }}
      >
        <ProFormText
          name="username"
          label="新用户名"
          rules={[{ required: true, message: '请输入用户名' }]}
        />
      </ModalForm>
    </PageContainer>
  );
}
